/**
 * React Query Provider
 * Настройка кеширования и управления серверным состоянием
 */

import { type ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

interface QueryProviderProps {
  children: ReactNode;
}

/**
 * Определяет имеет ли смысл ретраить эту ошибку.
 *
 * Ретраим ТОЛЬКО сетевые ошибки (не долетело / сервер временно недоступен) —
 * их повтор реально может помочь.
 *
 * НЕ ретраим:
 *   - 400 Bad Request — баг на клиенте, повтор не починит
 *   - 401 Unauthorized — юзер не залогинен, ретрай выдаст то же самое
 *   - 403 Forbidden — нет прав, тоже бесполезно
 *   - 404 Not Found — объекта нет, повтор ничего не изменит
 *   - 409 Conflict — конфликт состояния, надо обрабатывать а не ретраить
 *   - 422 Unprocessable — валидация, повтор не починит
 *
 * Ретраим:
 *   - 5xx ошибки сервера (временная проблема бэка)
 *   - TypeError "Failed to fetch" (обрыв сети)
 *   - AbortError / таймауты
 */
function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  // Максимум 1 повтор — дальше уже утомляет юзера ожиданием
  if (failureCount >= 1) return false;

  // Supabase/PostgREST ошибка: { status, code, message }
  if (error && typeof error === 'object') {
    const err = error as { status?: number; code?: string };

    // Явно 4xx — не ретраим
    if (typeof err.status === 'number' && err.status >= 400 && err.status < 500) {
      return false;
    }

    // Supabase коды типа PGRST116 (not found) тоже не ретраим
    if (err.code === 'PGRST116') return false;
  }

  // Всё остальное (5xx, fetch failed, timeout) — ретраим
  return true;
}

// Singleton QueryClient на уровне модуля — избегаем хуков в провайдере
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Данные считаются свежими 3 минуты (средняя стабильность)
      staleTime: 3 * 60 * 1000,
      // Кеш хранится 10 минут после последнего использования
      gcTime: 10 * 60 * 1000,
      // Условный retry — только сетевые ошибки, не 4xx
      retry: shouldRetryQuery,
      // Не рефетчить при фокусе окна (для мобильного приложения)
      refetchOnWindowFocus: false,
      // Рефетчить при восстановлении сети (важно для мобилки)
      refetchOnReconnect: true,
      // Рефетчить при монтировании если данные stale (обновление при возврате на экран)
      refetchOnMount: true,
    },
    mutations: {
      // Повторные попытки при ошибке мутации отключены —
      // мутации не идемпотентны, повторить значит выполнить дважды.
      retry: 0,
      // networkMode 'online' — мутация ждёт появления сети, не падает сразу оффлайн.
      // В мобильном приложении с флаки-WiFi это критично.
      networkMode: 'online',
    },
  },
});

export function QueryProvider({ children }: QueryProviderProps): JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
    </QueryClientProvider>
  );
}
